/**
 * ThinkingProcessCard - Elegant real-time process visibility
 *
 * This component renders the SOMA thinking/processing steps as users wait
 * for a response. Unlike the detailed run-log, this shows a high-level,
 * user-friendly view of what's happening.
 *
 * Design Philosophy:
 * - Show users the "why" not just the "what"
 * - Elegant step-by-step reveal with animations
 * - Build confidence while waiting
 * - Premium visual feedback
 *
 * Steps shown:
 * 1. Understanding request → Intent classification
 * 2. Routing → Which subsystem handles this
 * 3. Processing → AI generation in progress
 * 4. Verification → Quality/safety checks
 * 5. Complete → Response ready
 */

import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Brain,
    Router,
    Sparkles,
    ShieldCheck,
    CheckCircle2,
    Loader2,
    MessageSquare,
    Search,
    Database,
    Cpu,
} from 'lucide-react';

export interface ProcessStep {
    id: string;
    stage: string;
    action: string;
    message: string;
    timestamp: string;
    status: 'pending' | 'active' | 'complete' | 'error';
    metadata?: {
        confidence?: number;
        pass?: boolean;
        verifier?: string;
        score?: number;
    };
}

export interface ThinkingProcessData {
    traceId: string;
    status: 'RUNNING' | 'SUCCESS' | 'FAILED' | 'BLOCKED';
    mode: string;
    startedAt: string;
    steps: ProcessStep[];
    /** Intent classification result */
    intent?: {
        name: string;
        confidence: number;
    };
}

interface ThinkingProcessCardProps {
    data: ThinkingProcessData;
    className?: string;
    compact?: boolean;
}

/**
 * Maps raw step messages to user-friendly high-level descriptions
 */
const getStepDisplayInfo = (
    step: ProcessStep
): {
    icon: React.ReactNode;
    label: string;
    description: string;
    color: string;
} => {
    const message = (step.message || '').toLowerCase();
    const stage = step.stage?.toLowerCase() || '';

    // Understanding/Classification phase
    if (message.includes('processing your request') || stage.includes('init')) {
        return {
            icon: <Brain className="w-4 h-4" />,
            label: 'Understanding',
            description: 'Analyzing your request',
            color: 'text-blue-400',
        };
    }

    // Authentication
    if (message.includes('auth') || message.includes('authentication')) {
        return {
            icon: <ShieldCheck className="w-4 h-4" />,
            label: 'Authentication',
            description: 'Verifying access',
            color: 'text-emerald-400',
        };
    }

    // Routing
    if (message.includes('routing') || message.includes('router')) {
        return {
            icon: <Router className="w-4 h-4" />,
            label: 'Routing',
            description: 'Finding the best approach',
            color: 'text-violet-400',
        };
    }

    // Generation/Processing
    if (message.includes('generating') || message.includes('response')) {
        return {
            icon: <Sparkles className="w-4 h-4" />,
            label: 'Generating',
            description: 'Crafting intelligent response',
            color: 'text-amber-400',
        };
    }

    // Retrieval
    if (message.includes('retriev') || message.includes('search') || message.includes('lookup')) {
        return {
            icon: <Search className="w-4 h-4" />,
            label: 'Searching',
            description: 'Finding relevant information',
            color: 'text-cyan-400',
        };
    }

    // Database/Data access
    if (message.includes('database') || message.includes('data')) {
        return {
            icon: <Database className="w-4 h-4" />,
            label: 'Fetching Data',
            description: 'Accessing knowledge base',
            color: 'text-teal-400',
        };
    }

    // Verification
    if (
        message.includes('verify') ||
        message.includes('validator') ||
        step.stage === 'verification'
    ) {
        const pass = step.metadata?.pass;
        return {
            icon: <ShieldCheck className="w-4 h-4" />,
            label: 'Verification',
            description: pass
                ? `Quality check passed${step.metadata?.score ? ` (${Math.round(step.metadata.score * 100)}%)` : ''}`
                : 'Quality check in progress',
            color: pass ? 'text-emerald-400' : 'text-yellow-400',
        };
    }

    // Audit/Logging
    if (message.includes('audit') || message.includes('recording')) {
        return {
            icon: <MessageSquare className="w-4 h-4" />,
            label: 'Audit',
            description: 'Recording interaction',
            color: 'text-slate-400',
        };
    }

    // Completion
    if (message.includes('answer') || message.includes('complete')) {
        return {
            icon: <CheckCircle2 className="w-4 h-4" />,
            label: 'Complete',
            description: 'Response ready',
            color: 'text-emerald-400',
        };
    }

    // Default processing
    return {
        icon: <Cpu className="w-4 h-4" />,
        label: 'Processing',
        description: (step.message || '').slice(0, 50),
        color: 'text-slate-400',
    };
};

const stepVariants = {
    initial: { opacity: 0, x: -10 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 10 },
};

export const ThinkingProcessCard: React.FC<ThinkingProcessCardProps> = ({
    data,
    className = '',
    compact = false,
}) => {
    const isRunning = data.status === 'RUNNING';
    const isSuccess = data.status === 'SUCCESS';
    const isError = data.status === 'FAILED' || data.status === 'BLOCKED';

    // Deduplicate and group similar steps for cleaner display
    const displaySteps = useMemo(() => {
        const seen = new Set<string>();
        return data.steps.filter(step => {
            const info = getStepDisplayInfo(step);
            if (info.label === 'Complete' && data.status !== 'SUCCESS') {
                return false;
            }
            // Keep only one step per label (show the latest)
            if (seen.has(info.label)) return false;
            seen.add(info.label);
            return true;
        });
    }, [data.steps, data.status]);

    const formatTime = (isoString: string) => {
        try {
            return new Date(isoString).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        } catch {
            return '';
        }
    };

    const statusColor = isSuccess
        ? 'border-emerald-500/30 bg-emerald-500/5'
        : isError
          ? 'border-red-500/30 bg-red-500/5'
          : 'border-blue-500/30 bg-blue-500/5';

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`relative ${className}`}
        >
            <div
                className={`relative overflow-hidden rounded-xl border ${statusColor} backdrop-blur-sm`}
            >
                {/* Subtle animated gradient for running state */}
                {isRunning && (
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/5 to-transparent"
                        animate={{ x: ['-100%', '100%'] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    />
                )}

                {/* Header */}
                <div className="relative px-4 py-3 border-b border-slate-700/30">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {isRunning ? (
                                <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                            ) : isSuccess ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            ) : (
                                <Brain className="w-4 h-4 text-slate-400" />
                            )}
                            <span className="text-xs font-medium text-slate-300">
                                {isRunning
                                    ? 'Processing...'
                                    : isSuccess
                                      ? 'Complete'
                                      : 'Process Log'}
                            </span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-slate-500 font-mono">
                            <span>{data.mode}</span>
                            <span>{formatTime(data.startedAt)}</span>
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <div className={`relative ${compact ? 'px-4 py-2' : 'px-4 py-3'} space-y-2`}>
                    <AnimatePresence mode="popLayout">
                        {displaySteps.map((step, idx) => {
                            const info = getStepDisplayInfo(step);
                            const isLast = idx === displaySteps.length - 1;
                            const isActive = isLast && isRunning;

                            return (
                                <motion.div
                                    key={step.id || idx}
                                    variants={stepVariants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{ duration: 0.2, delay: idx * 0.05 }}
                                    className={`flex items-center gap-3 ${compact ? 'py-1' : 'py-1.5'}`}
                                >
                                    {/* Step indicator */}
                                    <div className="relative flex-shrink-0">
                                        {/* Connector line */}
                                        {idx < displaySteps.length - 1 && (
                                            <div className="absolute top-6 left-1/2 -translate-x-1/2 w-px h-4 bg-slate-700/50" />
                                        )}

                                        {/* Icon container */}
                                        <div
                                            className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                                                isActive
                                                    ? 'bg-blue-500/20 border border-blue-500/40'
                                                    : 'bg-slate-800/50 border border-slate-700/50'
                                            }`}
                                        >
                                            {isActive ? (
                                                <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />
                                            ) : (
                                                <span className={info.color}>{info.icon}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Step content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`text-xs font-medium ${isActive ? 'text-blue-300' : 'text-slate-300'}`}
                                            >
                                                {info.label}
                                            </span>
                                            {step.metadata?.pass !== undefined && (
                                                <span
                                                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                        step.metadata.pass
                                                            ? 'bg-emerald-500/10 text-emerald-400'
                                                            : 'bg-red-500/10 text-red-400'
                                                    }`}
                                                >
                                                    {step.metadata.pass ? 'PASS' : 'FAIL'}
                                                </span>
                                            )}
                                        </div>
                                        {!compact && (
                                            <p className="text-[11px] text-slate-500 truncate">
                                                {info.description}
                                            </p>
                                        )}
                                    </div>

                                    {/* Timestamp */}
                                    {!compact && (
                                        <span className="text-[10px] text-slate-600 flex-shrink-0">
                                            {formatTime(step.timestamp)}
                                        </span>
                                    )}
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>

                    {/* Empty state */}
                    {displaySteps.length === 0 && isRunning && (
                        <div className="flex items-center gap-2 py-2 text-xs text-slate-400">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Initializing...</span>
                        </div>
                    )}
                </div>

                {/* Intent badge */}
                {data.intent && (
                    <div className="px-4 pb-3 pt-1">
                        <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-slate-800/50 rounded-lg">
                            <Brain className="w-3 h-3 text-violet-400" />
                            <span className="text-[10px] text-slate-300">{data.intent.name}</span>
                            <span className="text-[10px] text-slate-500">
                                {Math.round(data.intent.confidence * 100)}%
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default ThinkingProcessCard;
